import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { formatTimeRu } from "../../../utils/appointmentLabels";
import { journalUrl } from "../../../utils/adminNav";
import L from "../../../constants/baysportLabels";
import s from "../clinic.module.css";
import { Button, Field, Input, Modal, Select, useToast } from "../../ui";

import SessionDateTimeFields from "./SessionDateTimeFields";
import CompetitionRegistrationsPanel from "./CompetitionRegistrationsPanel";
import {
  attendanceMarkPayload,
  attendanceMarkState,
  canMarkSessionAttendance,
  isEventSession,
  isIndividualSession,
  sessionScheduleFromIso,
  sessionScheduleToIso,
  sessionTimeState,
  sessionLabel,
} from "./calendarUtils";

export default function SessionDetailModal({
  sessionId,
  open,
  onClose,
  onUpdated,
  branches = [],
  doctors = [],
}) {
  const toast = useToast();
  const [journal, setJournal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("attendance");
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const data = await clinicAPI.getSessionJournal(sessionId);
      const sess = data.session;
      setJournal(sess);
      setEditForm({
        ...sessionScheduleFromIso(sess.start_datetime, sess.end_datetime),
        branch_id: sess.branch_id || sess.branch?.id || "",
        doctor_id: sess.doctor_id || sess.trainer?.id || "",
        notes: sess.notes || "",
        title: sess.title || "",
      });
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки");
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [sessionId, onClose, toast]);

  useEffect(() => {
    if (open && sessionId) {
      setMode("attendance");
      setCancelReason("");
      load();
    } else {
      setJournal(null);
    }
  }, [open, sessionId, load]);

  const mark = async (patientId, status, attendanceRow) => {
    if (!canMarkSessionAttendance(journal)) {
      toast.error("Занятие отменено");
      return;
    }
    const payload = attendanceRow
      ? attendanceMarkPayload(attendanceRow, status)
      : { status };
    try {
      const data = await clinicAPI.markSessionAttendance(sessionId, {
        patient_id: patientId,
        ...payload,
      });
      if (payload.clear) toast.success("Отметка снята");
      else if (status === "present" && data.attendance?.charge_error) {
        toast.error(data.attendance.charge_error);
      }
      await load();
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { start, end } = sessionScheduleToIso(editForm);
      await clinicAPI.updateTrainingSession(sessionId, {
        start_datetime: start,
        end_datetime: end,
        branch_id: editForm.branch_id || null,
        doctor_id: editForm.doctor_id,
        notes: editForm.notes,
        title: editForm.title || null,
      });
      toast.success("Изменения сохранены (только это занятие)");
      setMode("attendance");
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const cancelSession = async () => {
    if (
      !window.confirm(
        "Отменить это занятие? Регулярное расписание группы не изменится.",
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await clinicAPI.cancelTrainingSession(sessionId, {
        reason: cancelReason.trim() || undefined,
      });
      toast.success("Занятие отменено");
      onUpdated?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const isEvent = isEventSession(journal);
  const isIndividual = isIndividualSession(journal);
  const isCancelled = journal?.status === "cancelled";
  const isCompleted = journal?.status === "completed";
  const { isLive, isPast } = sessionTimeState(journal);
  const canMarkAttendance =
    Boolean(journal) && !isCancelled && canMarkSessionAttendance(journal);
  const canEditSchedule =
    canMarkAttendance && !isCompleted && !isPast;

  const journalLink = journal
    ? journalUrl({
        date: journal.start_datetime?.slice(0, 10),
        sessionId: journal.id,
        groupId: journal.group?.id,
      })
    : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        isEvent
          ? journal?.title || "Событие"
          : isIndividual
            ? sessionLabel(journal)
            : journal?.group?.name || L.session
      }
      footer={
        <div className={s.calModalFooter}>
          {journalLink ? (
            <Link to={journalLink} className={s.calModalLink}>
              Полный журнал →
            </Link>
          ) : null}
          <Button type="button" variant="secondary" onClick={onClose}>
            Закрыть
          </Button>
        </div>
      }
    >
      {loading || !journal ? (
        <p className={s.catalogMuted}>Загрузка…</p>
      ) : (
        <>
          <div className={s.calModalMeta}>
            <span>
              {formatTimeRu(journal.start_datetime)} —{" "}
              {formatTimeRu(journal.end_datetime)}
            </span>
            {journal.branch?.name ? <span>· {journal.branch.name}</span> : null}
            {isEvent ? (
              <span className={s.calEventBadge}>
                {journal.event_kind === "competition" ? "🏆 Соревнование" : "📌 Событие"}
              </span>
            ) : journal.group?.sport_direction ? (
              <span>· {journal.group.sport_direction}</span>
            ) : null}
            {isLive ? (
              <span className={s.calStatusLive}>Идёт сейчас</span>
            ) : null}
            {isPast && !isCompleted && !isCancelled ? (
              <span className={s.calStatusPast}>Занятие прошло</span>
            ) : null}
            {isCancelled ? (
              <span className={s.calStatusCancelled}>Отменено</span>
            ) : null}
            {isCompleted ? (
              <span className={s.calStatusDone}>Завершено</span>
            ) : null}
          </div>

          {(canMarkAttendance || canEditSchedule) ? (
            <div className={s.calDayPanelToolbar}>
              {!isEvent && canMarkAttendance ? (
                <button
                  type="button"
                  className={mode === "attendance" ? s.calTabActive : s.calTab}
                  onClick={() => setMode("attendance")}
                >
                  Перекличка
                </button>
              ) : null}
              {canEditSchedule ? (
                <>
                  <button
                    type="button"
                    className={mode === "edit" ? s.calTabActive : s.calTab}
                    onClick={() => setMode("edit")}
                  >
                    Перенести
                  </button>
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    disabled={saving}
                    onClick={cancelSession}
                  >
                    Отменить занятие
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}

          {mode === "edit" && canEditSchedule ? (
            <form onSubmit={saveEdit} className={s.calEditForm}>
              {isEvent ? (
                <Field label="Название">
                  <Input
                    value={editForm.title}
                    onChange={(e) =>
                      setEditForm({ ...editForm, title: e.target.value })
                    }
                  />
                </Field>
              ) : null}
              <SessionDateTimeFields
                date={editForm.date}
                startTime={editForm.startTime}
                endTime={editForm.endTime}
                onChange={(patch) => setEditForm({ ...editForm, ...patch })}
              />
              <Field label="Зал">
                <Select
                  value={editForm.branch_id}
                  onChange={(e) =>
                    setEditForm({ ...editForm, branch_id: e.target.value })
                  }
                >
                  <option value="">—</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={L.trainer}>
                <Select
                  value={editForm.doctor_id}
                  onChange={(e) =>
                    setEditForm({ ...editForm, doctor_id: e.target.value })
                  }
                >
                  {doctors.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.user?.full_name || d.full_name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Заметка">
                <Input
                  value={editForm.notes}
                  onChange={(e) =>
                    setEditForm({ ...editForm, notes: e.target.value })
                  }
                />
              </Field>
              <p className={s.docSectionHint}>
                Меняется только это занятие. Шаблон расписания группы не затрагивается.
              </p>
              <Button type="submit" disabled={saving}>
                {saving ? "…" : "Сохранить перенос"}
              </Button>
            </form>
          ) : null}

          {canEditSchedule && mode !== "edit" ? (
            <div className={s.calDayCancelField}>
              <Field label="Причина отмены (необязательно)">
                <Input
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Праздник, болезнь тренера…"
                />
              </Field>
            </div>
          ) : null}

          {mode === "attendance" && !isEvent && canMarkAttendance ? (
            <ul className={s.calAttendList}>
              {(journal.attendances || []).length === 0 ? (
                <li className={s.catalogMuted}>
                  В группе никого нет или состав ещё не подтянулся — обновите
                  страницу
                </li>
              ) : (
                journal.attendances.map((a) => {
                  const { isPresent } = attendanceMarkState(a);
                  return (
                    <li key={a.patient_id}>
                      <button
                        type="button"
                        className={`${s.calAttendChip} ${isPresent ? s.calAttendChip_on : ""}`}
                        onClick={() => mark(a.patient_id, "present", a)}
                      >
                        <span className={s.calAttendMark}>
                          {isPresent ? "✓" : ""}
                        </span>
                        {a.patient?.full_name || "—"}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          ) : null}

          {isEvent && !isCancelled ? (
            journal.event_kind === "competition" ? (
              <CompetitionRegistrationsPanel sessionId={sessionId} />
            ) : (
              <p className={s.docSectionHint}>
                {journal.notes || "Разовое событие без переклички группы."}
              </p>
            )
          ) : null}

          {isCancelled && journal.cancellation_reason ? (
            <p className={s.docSectionHint}>
              Причина отмены: {journal.cancellation_reason}
            </p>
          ) : null}
        </>
      )}
    </Modal>
  );
}
